/**
 * Garment measurements (the same rules as the SaaS app's @sable/domain). An item is measured as a top or as bottoms, and the
 * category decides which, so the form only shows what applies. Values are
 * stored on the item's `measurements` object with the unit they were taken in:
 *   { "garment": "top", "unit": "in", "pit_to_pit": 21, "length": 28 }
 */

export type Garment = "top" | "bottom";
export type LengthUnit = "in" | "cm";

export const MEASUREMENT_FIELDS: Record<Garment, readonly string[]> = {
  top: ["pit_to_pit", "length", "sleeve", "shoulder"],
  bottom: ["waist", "inseam", "rise", "hip", "leg_opening", "length"],
};

// Checked against the subcategory first, then the category: "activewear" says
// nothing, "leggings" says bottoms.
const BOTTOM = /\b(bottoms?|pants?|jeans?|shorts?|leggings?|joggers?|skirts?|skorts?|trousers?|chinos?|sweatpants?|capris?|culottes?|overalls?|trunks|briefs)\b/;
const TOP = /\b(tops?|shirts?|t-shirts?|tees?|blouses?|polos?|sweaters?|hoodies?|sweatshirts?|pullovers?|cardigans?|jackets?|coats?|vests?|tanks?|tank tops?|blazers?|outerwear|camis?|tunics?|crop tops?|jerseys?|long.sleeve|bras?|bodysuits?)\b/;

function classify(text: string | null | undefined): Garment | null {
  if (!text) return null;
  const t = text.toLowerCase().replace(/[_]/g, " ");
  if (BOTTOM.test(t)) return "bottom";
  if (TOP.test(t)) return "top";
  return null;
}

/** Top or bottoms from what the item is; null when it can't tell (a dress, a hat). */
export function garmentOf(category: string | null | undefined, subcategory: string | null | undefined): Garment | null {
  return classify(subcategory) ?? classify(category);
}

export function unitFor(units: "imperial" | "metric"): LengthUnit {
  return units === "metric" ? "cm" : "in";
}

/** The stored measurements for one garment type, dropping blanks. */
export function measurementsRecord(garment: Garment, unit: LengthUnit, values: Record<string, number | null>): Record<string, string | number> {
  const out: Record<string, string | number> = { garment, unit };
  for (const key of MEASUREMENT_FIELDS[garment]) {
    const v = values[key];
    if (v !== null && v !== undefined && Number.isFinite(v) && v > 0) out[key] = v;
  }
  return out;
}

/** Whether a measurements object has any actual measurement in it. */
export function hasMeasurements(m: Record<string, unknown> | null | undefined): boolean {
  if (!m) return false;
  return Object.entries(m).some(([k, v]) => k !== "garment" && k !== "unit" && typeof v === "number");
}
